/**
 * @file UI.jsx
 * @description Shared primitive UI components for ABAcases. Includes
 *   Badge, VerifiedBadge, PhoneInput, ZipInput, ScrollFade, EmptyState, and other
 *   reusable building blocks used across all views.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module components/UI
 */
// ─── Shared primitive components — Modern Redesign ───────────────────────────
import { useState, useEffect } from "react";
import { lookupZip } from "../utils/geo";
import { T, SIZE } from "../theme";
import { controlBox, appInput } from "./ui/appStyles";
import { AlertTriangleIcon, CheckIcon, PhoneIcon, MapPinIcon, InboxIcon } from "./Icons";
import {
  validatePhone, formatPhone,
  validateZip,  formatZip,
} from "../utils/validate";

// ─── Badge ───────────────────────────────────────────────────────────────────
export const Badge = ({ children, color, bg, style = {}, title }) => (
  <span title={title} style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11,
    fontFamily: T.fontBody,
    fontWeight: 600,
    letterSpacing: "0.01em",
    color,
    background: bg,
    padding: "2px 9px",
    borderRadius: T.radiusFull,
    border: `1px solid ${color}22`,
    whiteSpace: "nowrap",
    ...style,
  }}>
    {children}
  </span>
);

// ─── Chip ────────────────────────────────────────────────────────────────────
// Lighter cousin to Badge. Used across the app for *descriptive* metadata
// (case type, employment, skills, settings, payers) where Badge's chunky
// filled look would visually compete with adjacent state/alert badges.
//
// Rule of thumb:
//   - Use <Chip>  for things that describe what a case/applicant IS
//     (RBT, W2, Home setting, DTT skill, medicaid)
//   - Use <Badge> for things that signal what's HAPPENING / needs attention
//     (Due Soon, +3 new, Filled, Paused, MONITOR)
//
// Lifted from ApplicantComparisonView so the Applicants page and other
// cockpit surfaces share one visual vocabulary.
export const Chip = ({ children, color, bg, border, style = {}, title }) => (
  <span title={title} style={{
    display: "inline-flex", alignItems: "center", gap: 3,
    fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
    padding: "2px 7px", borderRadius: 6,
    color:  color  || T.textMid,
    background: bg || T.surfaceAlt,
    border: `1px solid ${border || T.border}`,
    whiteSpace: "nowrap",
    ...style,
  }}>
    {children}
  </span>
);

// ─── Btn ─────────────────────────────────────────────────────────────────────
// size="app": the app control height (theme.SIZE, 32 px; 40 px with touch) for app screens. Default keeps the larger
// public-page size.
export const Btn = ({ children, variant = "primary", onClick, style = {}, disabled = false, type = "button", size, touch = false }) => {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);

  const base = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    padding: "8px 18px",
    borderRadius: T.radiusFull,
    fontSize: 13,
    fontWeight: 600,
    fontFamily: T.fontBody,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: pressed && !disabled ? "scale(0.97)" : "scale(1)",
    opacity: disabled ? 0.5 : 1,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  const variants = {
    primary: {
      background: hovered ? T.blueHover : T.gradientBlue,
      color: "#ffffff",
      border: "none",
      padding: "10px 24px",
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: "0.01em",
      boxShadow: hovered ? T.shadowBlue : "0 2px 10px rgba(30,27,75,0.25)",
    },
    // Brand indigo: the single primary call-to-action color on public pages.
    cta: {
      background: hovered && !disabled ? T.indigoHover : T.gradientIndigo,
      color: "#ffffff",
      border: "none",
      padding: "10px 24px",
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: "0.01em",
      boxShadow: disabled ? "none" : hovered ? T.shadowIndigo : "0 2px 10px rgba(79,70,229,0.28)",
    },
    ghost: {
      background: hovered ? T.blueLight : "transparent",
      color: T.blue,
      border: `1.5px solid ${hovered ? T.blue : T.blue + "66"}`,
      boxShadow: "none",
    },
    outline: {
      background: hovered ? T.surfaceHover : T.surface,
      color: T.textMid,
      border: `1.5px solid ${hovered ? T.borderStrong : T.border}`,
      boxShadow: hovered ? T.shadow : "none",
      fontSize: 13,
      padding: "8px 18px",
    },
    danger: {
      background: hovered ? "#b91c1c" : "#dc2626",
      color: "#ffffff",
      border: "none",
      boxShadow: hovered ? "0 4px 14px rgba(220,38,38,0.35)" : "0 2px 6px rgba(220,38,38,0.2)",
    },
    success: {
      background: hovered ? T.greenHover : T.gradientGreen,
      color: "#ffffff",
      border: "none",
      boxShadow: hovered ? T.shadowGreen : "0 2px 8px rgba(5,150,105,0.20)",
    },
  };

  return (
    <button
      type={type}
      // Was styled-only (opacity + dropped onClick) without the real attribute,
      // so a "disabled" button still took focus and announced as enabled to
      // screen readers. Set it for real; the onClick guard stays as a backstop.
      disabled={disabled}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      style={{ ...base, ...variants[variant] || variants.primary, ...(size === "app" ? { ...controlBox({ touch }), fontSize: SIZE.text, letterSpacing: 0 } : null), ...style }}
    >
      {children}
    </button>
  );
};

// ─── Input ───────────────────────────────────────────────────────────────────
export const Input = ({ label, placeholder, value, onChange, type = "text", style = {}, prefix, suffix, disabled = false, min, max, step, list, inputMode, size, touch = false }) => {
  const [focused, setFocused] = useState(false);
  const app = size === "app";
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused ? T.blue : T.textMid,
          marginBottom: 6,
          fontFamily: T.fontBody,
          transition: "color 0.15s",
        }}>
          {label}
        </div>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {prefix && (
          <span style={{
            position: "absolute", left: 12, fontSize: 14,
            color: T.textLight, pointerEvents: "none",
          }}>{prefix}</span>
        )}
        <input
          type={type}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          list={list}
          inputMode={inputMode}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: `11px ${suffix ? "40px" : "14px"} 11px ${prefix ? "38px" : "14px"}`,
            border: `1.5px solid ${focused ? T.blue : T.border}`,
            borderRadius: T.radiusMd,
            fontSize: 13,
            color: disabled ? T.textLight : T.text,
            background: disabled ? T.surfaceAlt : "#ffffff",
            fontFamily: T.fontBody,
            outline: "none",
            cursor: disabled ? "not-allowed" : "text",
            boxSizing: "border-box",
            transition: "border-color 0.15s, box-shadow 0.15s",
            boxShadow: focused
              ? `0 0 0 3px ${T.blue}18, 0 1px 3px rgba(15,23,42,0.06)`
              : T.shadow,
            ...(app ? { ...appInput({ touch, focused, disabled }), paddingLeft: prefix ? 30 : 10, paddingRight: suffix ? 34 : 10 } : null),
            ...style,
          }}
        />
        {suffix && (
          <span style={{
            position: "absolute", right: 12, fontSize: 14,
            color: T.textLight, pointerEvents: "none",
          }}>{suffix}</span>
        )}
      </div>
    </div>
  );
};

// ─── Select ──────────────────────────────────────────────────────────────────
export const Select = ({ label, options, value, onChange, size, touch = false }) => {
  const [focused, setFocused] = useState(false);
  const app = size === "app";
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused ? T.blue : T.textMid,
          marginBottom: 6, fontFamily: T.fontBody,
          transition: "color 0.15s",
        }}>
          {label}
        </div>
      )}
      <select
        value={value}
        onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%",
          padding: "10px 14px",
          border: `1.5px solid ${focused ? T.blue : T.border}`,
          borderRadius: T.radiusMd,
          fontSize: 13,
          color: T.text,
          background: "#ffffff",
          fontFamily: T.fontBody,
          outline: "none",
          cursor: "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: focused ? `0 0 0 3px ${T.blue}18` : T.shadow,
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          paddingRight: 36,
          // background stays a plain color here so the chevron backgroundImage above survives.
          ...(app ? { ...appInput({ touch, focused }), background: "#ffffff", paddingRight: 30, backgroundPosition: "right 10px center" } : null),
        }}
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
};

// ─── Divider ─────────────────────────────────────────────────────────────────
export const Divider = ({ style = {} }) => (
  <div style={{ width: 1, height: 22, background: T.border, ...style }} />
);

// ─── Control Bar + Pills ─────────────────────────────────────────────────────
export const ControlBar = ({ label, children, style = {}, contentStyle = {} }) => (
  <div style={{
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    flexWrap: "wrap",
    padding: "10px 12px",
    borderRadius: 12,
    background: "#fbfbfc",
    border: `1px solid ${T.border}`,
    ...style,
  }}>
    {label && (
      <span style={{ fontSize: 11, color: T.textLight, fontWeight: 700, marginTop: 6, flexShrink: 0 }}>
        {label}
      </span>
    )}
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", minWidth: 0, ...contentStyle }}>
      {children}
    </div>
  </div>
);

export const FilterPill = ({ active = false, children, onClick, style = {} }) => (
  <button
    onClick={onClick}
    style={{
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      fontFamily: T.fontBody,
      cursor: "pointer",
      border: `1px solid ${active ? T.blue + "55" : T.border}`,
      background: active ? T.blueLight : "#fff",
      color: active ? T.blue : T.textMid,
      transition: "all 0.12s",
      ...style,
    }}
  >
    {children}
  </button>
);

// ─── Card ────────────────────────────────────────────────────────────────────
export const Card = ({ children, style = {}, onClick, hover = false }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hover && setHovered(true)}
      onMouseLeave={() => hover && setHovered(false)}
      style={{
        background: T.surface,
        border: `1px solid ${hovered ? T.borderStrong : T.border}`,
        borderRadius: T.radiusLg,
        padding: "20px",
        boxShadow: hovered ? T.shadowMd : T.shadow,
        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: hover && hovered ? "translateY(-1px)" : "none",
        cursor: onClick ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </div>
  );
};

// ─── SectionLabel ─────────────────────────────────────────────────────────────
export const SectionLabel = ({ children, style = {} }) => (
  <div style={{
    fontSize: 10,
    fontWeight: 700,
    color: T.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    fontFamily: T.fontBody,
    marginBottom: 8,
    ...style,
  }}>
    {children}
  </div>
);

// ─── SectionHeader ────────────────────────────────────────────────────────────
// Visually distinct section title with optional subtitle and accent rule.
export const SectionHeader = ({ title, subtitle, action, style = {} }) => (
  <div style={{ marginBottom: 20, ...style }}>
    <div style={{
      display: "flex", alignItems: "baseline",
      justifyContent: "space-between", gap: 12,
    }}>
      <div>
        <div style={{
          fontSize: 15, fontWeight: 700, color: T.text,
          fontFamily: T.fontBody, letterSpacing: "-0.01em",
        }}>{title}</div>
        {subtitle && (
          <div style={{
            fontSize: 12, color: T.textSubtle,
            marginTop: 2, fontFamily: T.fontBody, lineHeight: 1.5,
          }}>{subtitle}</div>
        )}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
    <div style={{
      marginTop: 10, height: 1,
      background: `linear-gradient(90deg, ${T.borderStrong} 0%, transparent 100%)`,
    }} />
  </div>
);

// ─── Stat ─────────────────────────────────────────────────────────────────────
export const Stat = ({ icon, label, value, sub, accent = T.blue }) => (
  <div style={{
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: T.radiusLg,
    padding: "20px",
    borderTop: `3px solid ${accent}`,
    boxShadow: T.shadow,
  }}>
    <div style={{ fontSize: 22, marginBottom: 10 }}>{icon}</div>
    <div style={{
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: 28, fontWeight: 700,
      color: T.text, lineHeight: 1,
    }}>{value}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid, marginTop: 6 }}>{label}</div>
    {sub && <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>{sub}</div>}
  </div>
);

// ─── EmptyState ───────────────────────────────────────────────────────────────
export const EmptyState = ({ icon = <InboxIcon size={34} color={T.textLight} />, title, desc, action }) => (
  <div style={{
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: "56px 32px", textAlign: "center",
  }}>
    {/* Icon container — gradient circle with subtle ring */}
    <div style={{
      width: 80, height: 80, borderRadius: "50%",
      background: `linear-gradient(145deg, ${T.blueLight} 0%, ${T.surfaceAlt} 100%)`,
      border: `1.5px solid ${T.border}`,
      boxShadow: T.shadow,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 34, marginBottom: 22,
    }}>{icon}</div>
    <div style={{
      fontSize: 17, fontWeight: 700, color: T.text,
      fontFamily: T.fontBody, letterSpacing: "-0.01em", marginBottom: 8,
    }}>{title}</div>
    <div style={{
      fontSize: 13, color: T.textSubtle,
      maxWidth: 300, lineHeight: 1.7,
      fontFamily: T.fontBody, marginBottom: action ? 24 : 0,
    }}>{desc}</div>
    {action}
  </div>
);

// ─── ValidatedInput ───────────────────────────────────────────────────────────
// Generic input with real-time inline validation feedback.
// validator: (value) => { valid: true|false|null, message: string }
export const ValidatedInput = ({
  label, placeholder, value, onChange, type = "text",
  validator, style = {}, prefix, suffix,
  id, name, autoComplete,
}) => {
  const [focused, setFocused] = useState(false);
  const result = validator ? validator(value) : { valid: null, message: "" };
  const borderColor = focused
    ? (result.valid === false ? "#dc2626" : result.valid === true ? T.green : T.blue)
    : (result.valid === false ? "#fca5a5" : result.valid === true ? T.green + "88" : T.border);
  const glowColor = result.valid === false
    ? "0 0 0 3px rgba(220,38,38,0.12)"
    : result.valid === true
      ? `0 0 0 3px ${T.green}18`
      : `0 0 0 3px ${T.blue}18`;

  return (
    <div style={{ marginBottom: 16, fontFamily: T.fontBody }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused ? (result.valid === false ? "#dc2626" : T.blue) : T.textMid,
          marginBottom: 6, fontFamily: T.fontBody, transition: "color 0.15s",
        }}>{label}</div>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {prefix && (
          <span style={{ position: "absolute", left: 12, fontSize: 14, color: T.textLight, pointerEvents: "none" }}>{prefix}</span>
        )}
        <input
          type={type}
          id={id}
          name={name}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: `11px ${suffix ? "40px" : "14px"} 11px ${prefix ? "38px" : "14px"}`,
            border: `1.5px solid ${borderColor}`,
            borderRadius: T.radiusMd,
            fontSize: 13, color: T.text,
            background: result.valid === false ? "#fff5f5" : "#ffffff",
            fontFamily: T.fontBody, outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
            boxShadow: focused ? glowColor : T.shadow,
            ...style,
          }}
        />
        {result.valid === true && !suffix && (
          <span style={{ position: "absolute", right: 12, color: T.green, pointerEvents: "none", display: "flex", alignItems: "center" }}><CheckIcon size={14} /></span>
        )}
        {result.valid === false && !suffix && (
          <span style={{ position: "absolute", right: 12, fontSize: 14, color: "#dc2626", pointerEvents: "none" }}>✕</span>
        )}
        {suffix && (
          <span style={{ position: "absolute", right: 12, fontSize: 14, color: T.textLight, pointerEvents: "none" }}>{suffix}</span>
        )}
      </div>
      {result.valid === false && result.message && (
        <div style={{
          fontSize: 11, color: "#dc2626", marginTop: 4,
          fontFamily: T.fontBody, display: "flex", alignItems: "center", gap: 4,
        }}>
          <AlertTriangleIcon size={13} style={{ flexShrink: 0 }} /> {result.message}
        </div>
      )}
      {result.valid === true && (
        <div style={{ fontSize: 11, color: T.green, marginTop: 4, fontFamily: T.fontBody }}>
          Looks good!
        </div>
      )}
    </div>
  );
};

// ─── PhoneInput ───────────────────────────────────────────────────────────────
// Auto-formats to (XXX) XXX-XXXX with real-time validation feedback.
export const PhoneInput = ({ label, value, onChange, placeholder = "(305) 555-0100", style = {} }) => {
  const [focused, setFocused] = useState(false);
  const result = validatePhone(value);
  const borderColor = focused
    ? (result.valid === false ? "#dc2626" : result.valid === true ? T.green : T.blue)
    : (result.valid === false ? "#fca5a5" : result.valid === true ? T.green + "88" : T.border);

  const handleChange = (e) => {
    const formatted = formatPhone(e.target.value);
    onChange({ ...e, target: { ...e.target, value: formatted } });
  };

  return (
    <div style={{ marginBottom: 16, fontFamily: T.fontBody }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused ? (result.valid === false ? "#dc2626" : T.blue) : T.textMid,
          marginBottom: 6, fontFamily: T.fontBody, transition: "color 0.15s",
        }}>{label}</div>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <span style={{ position: "absolute", left: 12, color: T.textLight, pointerEvents: "none", display: "flex", alignItems: "center" }}><PhoneIcon size={14} /></span>
        <input
          type="tel"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: "10px 40px 10px 38px",
            border: `1.5px solid ${borderColor}`,
            borderRadius: T.radiusMd,
            fontSize: 13, color: T.text,
            background: result.valid === false ? "#fff5f5" : "#ffffff",
            fontFamily: T.fontBody, outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
            boxShadow: focused
              ? (result.valid === false ? "0 0 0 3px rgba(220,38,38,0.12)" : result.valid === true ? `0 0 0 3px ${T.green}18` : `0 0 0 3px ${T.blue}18`)
              : T.shadow,
            ...style,
          }}
        />
        {result.valid === true && (
          <span style={{ position: "absolute", right: 12, color: T.green, pointerEvents: "none", display: "flex", alignItems: "center" }}><CheckIcon size={14} /></span>
        )}
        {result.valid === false && (
          <span style={{ position: "absolute", right: 12, fontSize: 14, color: "#dc2626", pointerEvents: "none" }}>✕</span>
        )}
      </div>
      {result.valid === false && result.message && (
        <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, fontFamily: T.fontBody, display: "flex", alignItems: "center", gap: 4 }}>
          <AlertTriangleIcon size={13} style={{ flexShrink: 0 }} /> {result.message}
        </div>
      )}
      {result.valid === true && (
        <div style={{ fontSize: 11, color: T.green, marginTop: 4, fontFamily: T.fontBody }}>Looks good!</div>
      )}
    </div>
  );
};

// ─── ZipInput ─────────────────────────────────────────────────────────────────
// Auto-formats ZIP and ZIP+4. When a valid 5-digit ZIP is entered it calls
// the free zippopotam.us API and fires onLookup({ city, state }) so the
// parent form can auto-fill those fields. Shows a "📍 City, ST" pill inline.
export const ZipInput = ({ label, value, onChange, onLookup, onLookupComplete, placeholder = "33101", style = {}, showGeoPill = true }) => {
  const [focused,  setFocused]  = useState(false);
  const [geo,      setGeo]      = useState(null);   // null | "loading" | { city, state } | "error"
  const result = validateZip(value);

  // Trigger lookup whenever we have a clean 5-digit ZIP
  useEffect(() => {
    const raw = value.replace(/\D/g, "");
    if (raw.length !== 5) { setGeo(null); onLookupComplete?.(false); return; }
    let cancelled = false;
    setGeo("loading");
    lookupZip(raw).then(r => {
      if (cancelled) return;
      if (r) { setGeo(r); onLookup?.(r); onLookupComplete?.(true); }
      else   { setGeo("error"); onLookupComplete?.(false); }
    });
    return () => { cancelled = true; };
  }, [value]); // eslint-disable-line

  const borderColor = focused
    ? (result.valid === false ? "#dc2626" : result.valid === true ? T.green : T.blue)
    : (result.valid === false ? "#fca5a5" : result.valid === true ? T.green + "88" : T.border);

  const handleChange = (e) => {
    const formatted = formatZip(e.target.value);
    onChange({ ...e, target: { ...e.target, value: formatted } });
  };

  return (
    <div style={{ marginBottom: 16, fontFamily: T.fontBody }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused ? (result.valid === false ? "#dc2626" : T.blue) : T.textMid,
          marginBottom: 6, fontFamily: T.fontBody, transition: "color 0.15s",
        }}>{label}</div>
      )}
      <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
        <span style={{ position: "absolute", left: 12, color: T.textLight, pointerEvents: "none", display: "flex", alignItems: "center" }}><MapPinIcon size={14} /></span>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          maxLength={10}
          style={{
            width: "100%",
            padding: "10px 40px 10px 38px",
            border: `1.5px solid ${borderColor}`,
            borderRadius: T.radiusMd,
            fontSize: 13, color: T.text,
            background: result.valid === false ? "#fff5f5" : "#ffffff",
            fontFamily: T.fontBody, outline: "none",
            boxSizing: "border-box",
            transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
            boxShadow: focused
              ? (result.valid === false ? "0 0 0 3px rgba(220,38,38,0.12)" : result.valid === true ? `0 0 0 3px ${T.green}18` : `0 0 0 3px ${T.blue}18`)
              : T.shadow,
            ...style,
          }}
        />
        {/* Right icon: spinner → check → error */}
        {geo === "loading" && (
          <span style={{ position: "absolute", right: 12, fontSize: 13, color: T.textLight, pointerEvents: "none",
            animation: "spin 0.9s linear infinite", display: "inline-block" }}>⟳</span>
        )}
        {geo && geo !== "loading" && geo !== "error" && (
          <span style={{ position: "absolute", right: 12, fontSize: 14, color: T.green, pointerEvents: "none" }}>✓</span>
        )}
        {result.valid === false && (
          <span style={{ position: "absolute", right: 12, fontSize: 14, color: "#dc2626", pointerEvents: "none" }}>✕</span>
        )}
      </div>

      {/* Geo pill — shown when city/state resolved (callers can suppress it) */}
      {showGeoPill && geo && geo !== "loading" && geo !== "error" && (
        <div style={{
          marginTop: 6, display: "inline-flex", alignItems: "center", gap: 5,
          background: T.greenLight, border: `1px solid ${T.green}33`,
          borderRadius: T.radiusFull, padding: "3px 10px",
          fontSize: 11, fontWeight: 600, color: T.green,
          fontFamily: T.fontBody,
          animation: "fadeIn 0.2s ease",
        }}>
          <MapPinIcon size={13} /> {geo.city}, {geo.state} (auto-filled)
        </div>
      )}

      {geo === "error" && result.valid !== false && (
        <div style={{ fontSize: 11, color: T.textLight, marginTop: 4, fontFamily: T.fontBody }}>
          ZIP not found. City/state not auto-filled.
        </div>
      )}
      {result.valid === false && result.message && (
        <div style={{ fontSize: 11, color: "#dc2626", marginTop: 4, fontFamily: T.fontBody, display: "flex", alignItems: "center", gap: 4 }}>
          <AlertTriangleIcon size={13} style={{ flexShrink: 0 }} /> {result.message}
        </div>
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes fadeIn { from { opacity:0; transform:translateY(-4px); } to { opacity:1; transform:translateY(0); } }`}</style>
    </div>
  );
};

// ─── StateSelect ──────────────────────────────────────────────────────────────
// Full 50-state US dropdown with territory options.
export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
  "DC","PR","VI","GU",
];

// ── Canonical vocabularies ────────────────────────────────────────────────────
// Single source of truth for age groups and languages so therapist setup, the
// profile editor, case posting, and the agency filters all agree. Previously
// each surface kept its own list (case posting had 15 languages, therapist
// setup 8) and the filters derived messy, overlapping options from raw data.
export const AGE_GROUPS = ["2–5", "6–10", "11–14", "15–18", "Adults"];
export const LANGUAGES = [
  "English", "Spanish", "Portuguese", "Haitian Creole",
  "French", "Mandarin", "Arabic", "Sign Language",
];

/**
 * Month + Year picker emitting a "YYYY-MM" string.
 *
 * Replaces <input type="month">, which Safari has never supported — there it
 * silently degrades to a plain text box (no picker, no masking), letting free
 * text reach therapist_profiles.credentialed_date, a real Postgres `date`.
 * Two native <select>s render and behave identically in every browser AND make
 * an invalid value unrepresentable, so validation becomes a backstop rather
 * than the only defense.
 *
 * Month names are localized via Intl from the `locale` prop (UI.jsx stays free
 * of the i18n dependency by design — callers pass i18n.language).
 *
 * @param {string}   value     - "YYYY-MM", or "" when unset
 * @param {function} onChange  - receives "YYYY-MM" or ""
 * @param {string}   [maxMonth]- "YYYY-MM" ceiling; later months are disabled
 * @param {string}   [locale]  - BCP-47 tag for month names (default: runtime)
 * @param {boolean}  [hasError]- render the error border
 */
export const MonthYearPicker = ({ value, onChange, maxMonth, locale, hasError = false, disabled = false }) => {
  const [focused, setFocused] = useState(false);
  const MIN_YEAR = 1960;
  // Partial selections live in local state. A "YYYY-MM" value can only be
  // emitted once BOTH halves are chosen, so if the selects read straight off
  // the (still-empty) parent value, picking the first dropdown would instantly
  // revert — making the control impossible to use.
  const parsed  = /^(\d{4})-(\d{2})$/.exec(String(value || "")) || [];
  const [selYear, setSelYear] = useState(parsed[1] || "");
  const [selMon,  setSelMon]  = useState(parsed[2] || "");

  // Sync down only when the parent supplies a complete value (initial load,
  // profile fetch, external reset) — never clobber an in-progress selection.
  useEffect(() => {
    const p = /^(\d{4})-(\d{2})$/.exec(String(value || "")) || [];
    if (p[1]) { setSelYear(p[1]); setSelMon(p[2]); }
  }, [value]);

  const now      = new Date();
  const maxYear  = maxMonth ? parseInt(maxMonth.slice(0, 4), 10) : now.getFullYear();
  const maxMonNo = maxMonth ? parseInt(maxMonth.slice(5, 7), 10) : 12;

  const years = [];
  for (let y = maxYear; y >= MIN_YEAR; y--) years.push(String(y));

  const monthNames = [];
  {
    let fmt;
    try { fmt = new Intl.DateTimeFormat(locale || undefined, { month: "long" }); }
    catch (_) { fmt = new Intl.DateTimeFormat(undefined, { month: "long" }); }
    for (let i = 0; i < 12; i++) monthNames.push(fmt.format(new Date(2000, i, 1)));
  }

  // Local state always updates; the parent only hears about complete values
  // (and "" when the pair becomes incomplete, so a half-selection never
  // masquerades as saved data).
  const commit = (y, m) => {
    setSelYear(y);
    setSelMon(m);
    onChange(y && m ? `${y}-${m}` : "");
  };

  const handleYear = (y) => {
    // Moving to the ceiling year can strand a now-future month — drop it
    // rather than emitting a date the user never chose.
    const stranded = y && selMon && parseInt(y, 10) === maxYear && parseInt(selMon, 10) > maxMonNo;
    commit(y, stranded ? "" : selMon);
  };

  const selectStyle = {
    width: "100%",
    padding: "10px 32px 10px 12px",
    border: `1.5px solid ${hasError ? "#dc2626" : (focused && !disabled ? T.blue : T.border)}`,
    borderRadius: 10,
    fontSize: 13,
    color: disabled ? T.textMid : T.text,
    background: disabled ? T.surfaceAlt : "#fff",
    fontFamily: T.fontBody,
    outline: "none",
    cursor: disabled ? "default" : "pointer",
    boxSizing: "border-box",
    transition: "border-color 0.15s",
    appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 8 }}>
      <select
        aria-label="Month"
        value={selMon}
        disabled={disabled}
        onChange={e => commit(selYear, e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={selectStyle}
      >
        <option value="">Month</option>
        {monthNames.map((name, i) => {
          const mNo = i + 1;
          const future = selYear && parseInt(selYear, 10) === maxYear && mNo > maxMonNo;
          return (
            <option key={name} value={String(mNo).padStart(2, "0")} disabled={!!future}>
              {name}
            </option>
          );
        })}
      </select>
      <select
        aria-label="Year"
        value={selYear}
        disabled={disabled}
        onChange={e => handleYear(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={selectStyle}
      >
        <option value="">Year</option>
        {years.map(y => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
};

export const StateSelect = ({ label, value, onChange, style = {}, disabled = false }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: 16, fontFamily: T.fontBody }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600,
          color: focused && !disabled ? T.blue : T.textMid,
          marginBottom: 6, fontFamily: T.fontBody, transition: "color 0.15s",
        }}>{label}</div>
      )}
      <select
        value={value}
        onChange={onChange}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          width: "100%",
          padding: "10px 36px 10px 14px",
          border: `1.5px solid ${focused && !disabled ? T.blue : T.border}`,
          borderRadius: T.radiusMd,
          fontSize: 13, color: value ? (disabled ? T.textMid : T.text) : T.textLight,
          background: disabled ? T.surfaceAlt : T.surface,
          fontFamily: T.fontBody, outline: "none",
          cursor: disabled ? "default" : "pointer",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: focused && !disabled ? `0 0 0 3px ${T.blue}18` : T.shadow,
          appearance: "none",
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2364748b' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 12px center",
          ...style,
        }}
      >
        <option value="">— Select State —</option>
        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );
};

// ─── ExperienceStepper ────────────────────────────────────────────────────────
// Numeric stepper for years of experience (0–40).
export const ExperienceStepper = ({ label, value, onChange, min = 0, max = 40, style = {} }) => {
  const num = parseInt(value, 10) || 0;
  const decrement = () => { if (num > min) onChange(String(num - 1)); };
  const increment = () => { if (num < max) onChange(String(num + 1)); };

  const btnStyle = (disabled) => ({
    width: 34, height: 34, borderRadius: "50%",
    border: `1.5px solid ${disabled ? T.border : T.blue + "66"}`,
    background: disabled ? T.surfaceAlt : T.blueLight,
    color: disabled ? T.textXLight : T.blue,
    fontSize: 18, fontWeight: 700, lineHeight: 1,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s",
    flexShrink: 0,
    fontFamily: T.fontBody,
  });

  return (
    <div style={{ marginBottom: 16, fontFamily: T.fontBody, ...style }}>
      {label && (
        <div style={{
          fontSize: 12, fontWeight: 600, color: T.textMid,
          marginBottom: 8, fontFamily: T.fontBody,
        }}>{label}</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 12, height: 44 }}>
        <button onClick={decrement} disabled={num <= min} style={btnStyle(num <= min)}>−</button>
        <div style={{
          flex: 1, textAlign: "center",
          fontFamily: T.fontMono,
          fontSize: 22, fontWeight: 700, color: T.text,
          letterSpacing: "-0.02em",
        }}>
          {num}
          <span style={{
            fontSize: 12, fontWeight: 500, color: T.textLight,
            fontFamily: T.fontBody, marginLeft: 4,
          }}>
            {num === 1 ? "yr" : "yrs"}
          </span>
        </div>
        <button onClick={increment} disabled={num >= max} style={btnStyle(num >= max)}>+</button>
      </div>
      {num === 0 && (
        <div style={{ fontSize: 11, color: T.textLight, marginTop: 4, fontFamily: T.fontBody }}>
          Entry level / student
        </div>
      )}
    </div>
  );
};

// ─── SaveBanner ───────────────────────────────────────────────────────────────
// Temporary success confirmation banner. Parent controls visibility with `show`.
// Auto-dismisses via parent timeout (this component just handles animation).
export const SaveBanner = ({ show, message = "Changes saved", style = {} }) => (
  <div style={{
    position: "fixed", bottom: 28, left: "50%",
    transform: `translateX(-50%) translateY(${show ? 0 : 80}px)`,
    opacity: show ? 1 : 0,
    transition: "transform 0.3s cubic-bezier(0.34,1.56,0.64,1), opacity 0.25s ease",
    background: T.gradientGreen,
    color: "#fff",
    padding: "11px 28px",
    borderRadius: T.radiusFull,
    fontSize: 13, fontWeight: 700,
    fontFamily: T.fontBody,
    boxShadow: T.shadowGreen,
    display: "flex", alignItems: "center", gap: 10,
    zIndex: 9000,
    pointerEvents: "none",
    whiteSpace: "nowrap",
    ...style,
  }}>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
    {message}
  </div>
);

// ─── SampleBadge ─────────────────────────────────────────────────────────────
// Marks illustrative mockups on public pages ("Sample") so a sample dashboard or
// case card is never mistaken for live data. Callers pass the translated label.
export const SampleBadge = ({ children, dark = false, style = {} }) => (
  <span data-testid="sample-badge" style={{
    display: "inline-flex", alignItems: "center",
    fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase",
    fontFamily: T.fontBody, whiteSpace: "nowrap",
    color: dark ? "#c7d2fe" : "#4338ca",
    background: dark ? "rgba(165,180,252,0.16)" : "#eef2ff",
    border: `1px solid ${dark ? "rgba(165,180,252,0.4)" : "#c7d2fe"}`,
    borderRadius: 99, padding: "3px 9px",
    ...style,
  }}>
    {children}
  </span>
);
